import { collection, addDoc, serverTimestamp, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

/** Firestore rejects `undefined`; omit those keys before writes. */
function omitUndefined<T extends object>(data: T): T {
  return Object.fromEntries(
    Object.entries(data).filter(([, v]) => v !== undefined)
  ) as T;
}

export interface FormSubmission {
  // Personal Information
  name: string;
  birthYear: string;
  emiratesOfResidence: string;
  referredBy: string;
  phoneNumber: string;
  linkedinAccount: string;
  email: string;
  
  // Academic Information
  educationLevel: string;
  fieldOfStudy: string;
  otherFieldOfStudy: string;
  coursesCompleted: string;
  universityName: string;
  
  // Professional Information
  currentPosition: string;
  companyName: string;
  lookingForJob: boolean;
  professionalJourney: string;
  communityHelpedEmployment: string;
  skills: string[];
  
  // Additional Information
  interestedInSpeaking: string;
  speakingTopics: string;
  interestedInEntrepreneurship: string;
  hasProjectIdea: string;
  projectIdeaDescription: string;
  interestedInVolunteering: string;
  volunteerTeam: string;
  generalNote: string;
  
  // Metadata
  submittedAt: any; // Firestore timestamp
  status: 'pending' | 'reviewed' | 'approved' | 'rejected';
  addedByAdmin?: boolean;
  id?: string; // Document ID from Firestore
}

export interface ContactForm {
  id?: string;
  name: string;
  email: string;
  message: string;
  submittedAt: any; // Firestore timestamp
  status: 'new' | 'read' | 'replied';
}

export const submitFormToFirebase = async (formData: Omit<FormSubmission, 'submittedAt' | 'status'>): Promise<string> => {
  try {
    const submissionData: FormSubmission = {
      ...formData,
      skills: formData.skills || [], // Ensure skills array is always present
      submittedAt: serverTimestamp(),
      status: 'pending'
    };

    const docRef = await addDoc(collection(db, 'formSubmissions'), submissionData);
    console.log('Form submitted successfully with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error submitting form to Firebase:', error);
    throw new Error('Failed to submit form. Please try again.');
  }
};

export const getFormSubmissions = async () => {
  try {
    // This would be used for admin panel to view submissions
    // Implementation depends on your admin requirements
    console.log('Getting form submissions...');
  } catch (error) {
    console.error('Error getting form submissions:', error);
    throw error;
  }
};

export const deleteFormSubmission = async (submissionId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'formSubmissions', submissionId));
  } catch (error) {
    console.error('Error deleting form submission:', error);
    throw new Error('Failed to delete form submission.');
  }
};

export type AdminFormSubmissionInput = {
  name: string;
  phoneNumber: string;
} & Partial<Omit<FormSubmission, 'name' | 'phoneNumber' | 'submittedAt' | 'status' | 'id'>>;

export const createAdminFormSubmission = async (
  formData: AdminFormSubmissionInput
): Promise<string> => {
  try {
    const submissionData = omitUndefined({
      ...formData,
      skills: formData.skills || [],
      lookingForJob: formData.lookingForJob ?? false,
      submittedAt: serverTimestamp(),
      status: 'approved' as const,
      addedByAdmin: true,
    });

    const docRef = await addDoc(collection(db, 'formSubmissions'), submissionData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating admin form submission:', error);
    throw new Error('Failed to add person. Please try again.');
  }
}; 

export interface Event {
  id?: string;
  name: string;
  date: string;
  description: string;
  active: boolean;
}

export interface Meetup {
  id?: string;
  title: string;
  date: string;
  description: string;
  speakerName: string;
  speakerRole: string;
  imageUrl?: string;
  imageName?: string;
  link?: string;
  location?: string;
  numberOfAttendees?: number;
  createdAt: any;
  updatedAt: any;
}

export const createEvent = async (eventData: Omit<Event, 'id' | 'active'>): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, 'events'), { ...eventData, active: true });
    return docRef.id;
  } catch (error) {
    console.error('Error creating event:', error);
    throw new Error('Failed to create event.');
  }
};

export const updateEventActive = async (eventId: string, active: boolean): Promise<void> => {
  try {
    await updateDoc(doc(db, 'events', eventId), { active });
  } catch (error) {
    console.error('Error updating event active status:', error);
    throw new Error('Failed to update event status.');
  }
};

export interface EventResponse {
  id?: string;
  name: string;
  email: string;
  linkedin: string;
  country: string;
  speciality: string;
  submittedAt: any;
}

export const getEventResponses = async (eventId: string): Promise<EventResponse[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'events', eventId, 'responses'));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as EventResponse[];
  } catch (error) {
    console.error('Error fetching event responses:', error);
    throw new Error('Failed to fetch event responses.');
  }
};

export const getEvents = async (): Promise<Event[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'events'));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Event[];
  } catch (error) {
    console.error('Error fetching events:', error);
    throw new Error('Failed to fetch events.');
  }
}; 

// Meetup Management Functions
export const uploadMeetupImage = async (file: File): Promise<{ url: string; name: string }> => {
  try {
    const timestamp = Date.now();
    const fileName = `meetups/${timestamp}_${file.name}`;
    const storageRef = ref(storage, fileName);
    
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    
    return { url, name: fileName };
  } catch (error) {
    console.error('Error uploading image:', error);
    throw new Error('Failed to upload image.');
  }
};

export const createMeetup = async (meetupData: Omit<Meetup, 'id' | 'createdAt' | 'updatedAt'>, imageFile?: File): Promise<string> => {
  try {
    let imageUrl = '';
    let imageName = '';
    
    if (imageFile) {
      const uploadResult = await uploadMeetupImage(imageFile);
      imageUrl = uploadResult.url;
      imageName = uploadResult.name;
    }
    
    const meetupDataWithMetadata: Omit<Meetup, 'id'> = {
      ...meetupData,
      imageUrl,
      imageName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    const docRef = await addDoc(collection(db, 'meetups'), omitUndefined(meetupDataWithMetadata));
    return docRef.id;
  } catch (error) {
    console.error('Error creating meetup:', error);
    throw new Error('Failed to create meetup.');
  }
};

export const getMeetups = async (): Promise<Meetup[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'meetups'));
    const meetups = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Meetup);
    
    // Sort by date (newest to oldest)
    return meetups.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });
  } catch (error) {
    console.error('Error fetching meetups:', error);
    throw new Error('Failed to fetch meetups.');
  }
};

export const updateMeetup = async (meetupId: string, meetupData: Partial<Omit<Meetup, 'id' | 'createdAt'>>, imageFile?: File): Promise<void> => {
  try {
    let updateData: Partial<Meetup> = {
      ...meetupData,
      updatedAt: serverTimestamp(),
    };
    
    if (imageFile) {
      const uploadResult = await uploadMeetupImage(imageFile);
      updateData.imageUrl = uploadResult.url;
      updateData.imageName = uploadResult.name;
    }
    
    await updateDoc(doc(db, 'meetups', meetupId), omitUndefined(updateData));
  } catch (error) {
    console.error('Error updating meetup:', error);
    throw new Error('Failed to update meetup.');
  }
};

export const deleteMeetup = async (meetupId: string, imageName?: string): Promise<void> => {
  try {
    // Delete the meetup document
    await deleteDoc(doc(db, 'meetups', meetupId));
    
    // Delete the image from storage if it exists
    if (imageName) {
      const imageRef = ref(storage, imageName);
      await deleteObject(imageRef);
    }
  } catch (error) {
    console.error('Error deleting meetup:', error);
    throw new Error('Failed to delete meetup.');
  }
};

// Contact Form Functions
export const submitContactForm = async (contactData: Omit<ContactForm, 'id' | 'submittedAt' | 'status'>): Promise<string> => {
  try {
    const contactDataWithMetadata: Omit<ContactForm, 'id'> = {
      ...contactData,
      submittedAt: serverTimestamp(),
      status: 'new'
    };
    
    const docRef = await addDoc(collection(db, 'contactForms'), contactDataWithMetadata);
    return docRef.id;
  } catch (error) {
    console.error('Error submitting contact form:', error);
    throw new Error('Failed to submit contact form.');
  }
};

export const getContactForms = async (): Promise<ContactForm[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'contactForms'));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as ContactForm);
  } catch (error) {
    console.error('Error fetching contact forms:', error);
    throw new Error('Failed to fetch contact forms.');
  }
};

export const updateContactFormStatus = async (contactId: string, status: ContactForm['status']): Promise<void> => {
  try {
    await updateDoc(doc(db, 'contactForms', contactId), { status });
  } catch (error) {
    console.error('Error updating contact form status:', error);
    throw new Error('Failed to update contact form status.');
  }
};

export const deleteContactForm = async (contactId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'contactForms', contactId));
  } catch (error) {
    console.error('Error deleting contact form:', error);
    throw new Error('Failed to delete contact form.');
  }
}; 